import { defineStore } from 'pinia';
import { ref } from 'vue';

import useUserComposable from '@/composables/UserComposable';

import GlobalRequest from '@/api/global-request';

import { showToast } from '@/helpers/toast-handler';

import { SharedTypes } from '@/enums';
import { getPage, getPerPage } from '@/composables/PaginationComposable';

const COMPANY_URL = 'v2/template/blocks';
const SHARED_URL = 'v2/template/blocks/shared';

export const useBlockStore = defineStore('block', () => {
    const { company_id } = useUserComposable();
    const loading = ref(false);
    const block = ref(null);
    const blocks = ref([]);
    const totalBlocks = ref(0);
    const params = ref({
        categories: [],
        code: null,
        code_exact: null,
        company_id: null,
        default: null,
    });

    const fetchAllBlocks = async (shared, params, list) => {
        try {
            let page_size = getPerPage(list)
            let page = getPage(list)

            console.log('fetchAllBlocks', shared, params, list)

            let paginate = true

            if (typeof list === 'undefined') {
                paginate = false
            }

            loading.value = true;
            const response = await GlobalRequest.getRequest(COMPANY_URL, {
                params: {
                    page_size: page_size,
                    page: page + 1,
                    paginate: paginate,
                    categories: params.categories,
                    code: params.code,
                    code_exact: params.code_exact,
                    company_id: shared == SharedTypes.COMPANY ? company_id.value : null,
                    default: params.default,
                },
            });

            totalBlocks.value = response.data.total;

            if (paginate) {
                blocks.value = response.data?.data?.filter((block) => block.code !== 'user_block');
            } else {
                blocks.value = response.data?.filter((block) => block.code !== 'user_block');
            }

            return blocks.value;
        } catch (error) {
            showToast(error.response, 'Failed to fetch company blocks');
        } finally {
            loading.value = false;
        }
    };

    const createBlock = async (shared, body) => {
        try {
            const response = await GlobalRequest.postRequest(shared == SharedTypes.SHARED ? SHARED_URL : COMPANY_URL, {
                ...body,
                company_id: company_id.value,
                description: body.description?.length > 255 ? body.description?.substring(0, 255) : body.description,
            });
            if (response.statusCode === 201) {
                block.value = response.data;
                if (body.draft == false) {
                    showToast(response, 'Block created successfully');
                    // push it into the array
                    blocks.value.push(block.value);
                }
                return response.data;
            }
        } catch (error) {
            const message = getApiErrorMessage(error);
            showToast({}, message);
        }
    };

    const updateBlock = async (id, shared, body) => {
        try {
            const URL = shared == SharedTypes.SHARED ? SHARED_URL : COMPANY_URL;
            const response = await GlobalRequest.patchRequest(`${URL}/${id}`, {
                ...body,
                company_id: company_id.value,
                description: body.description?.length > 255 ? body.description?.substring(0, 255) : body.description,
            });
            if (body.draft == false) {
                showToast(response, 'Block updated successfully');
                return true;
            }
            return false;
        } catch (error) {
            const message = getApiErrorMessage(error);
            showToast({}, message);
        }
    };

    /**
 * Extracts a readable error message from a Laravel/Axios API error.
 * Always returns a string suitable for showing in toast.
 * @param {any} error - Axios error object
 * @returns {string} - The error message
 */
function getApiErrorMessage(error) {
    let message = "Something went wrong";

    // 1️⃣ Check Laravel validation errors (nested under response.data.data)
    if (error?.response?.data?.data && typeof error.response.data.data === "object") {
        const data = error.response.data.data;
        const firstField = Object.keys(data)[0];
        if (firstField && Array.isArray(data[firstField]) && data[firstField].length > 0) {
            message = data[firstField][0];
            return message;
        }
    }

    // 2️⃣ Check Laravel general error message (response.data.message)
    if (error?.response?.data?.message) {
        return error.response.data.message;
    }

    // 3️⃣ Check if error data is directly on the error object (some wrappers)
    if (error?.data?.data && typeof error.data.data === "object") {
        const data = error.data.data;
        const firstField = Object.keys(data)[0];
        if (firstField && Array.isArray(data[firstField]) && data[firstField].length > 0) {
            message = data[firstField][0];
            return message;
        }
    }

    // 4️⃣ Check if error.data.message exists
    if (error?.data?.message) {
        return error.data.message;
    }

    return message;
}


    const fetchBlockDetails = async (shared, id) => {
        try {
            const response = await GlobalRequest.getRequest((shared == 'Shared' ? SHARED_URL : COMPANY_URL) + '/' + id);
            block.value = response.data;
            return response.data;
        } catch (error) {
            showToast(error.response, 'Failed to fetch block details');
        }
    };

    const deleteBlock = async (shared, id) => {
        try {
            const response = await GlobalRequest.deleteRequest(`${COMPANY_URL}/${id}`, {
                params: {
                    company_id: shared == SharedTypes.COMPANY ? company_id.value : null,
                },
            });
            if (response.statusCode === 200) {
                showToast(response, 'Block deleted successfully');
                blocks.value = blocks.value.filter((block) => block.id !== id);
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete the block');
        }
    };

    const setBlockDefault = async (id, shared) => {
        try {
            const response = await GlobalRequest.patchRequest((shared == 'Shared' ? SHARED_URL : COMPANY_URL) + '/' + id + '/default');
            if (response.statusCode === 200) {
                showToast(response, 'Block set as default successfully');
                fetchAllBlocks(shared, params.value);
            }
            return response.data;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to set the block as default');
        }
    };

    const importSharedBlocksToCompany = async (blockId, companyId) => {
        try {
            const response = await GlobalRequest.patchRequest(`${SHARED_URL}/${blockId}/import?company_id=${companyId}`);
            if (response.statusCode === 201) {
                return true;
            }
            return false;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to import shared blocks');
        }
    };

    const exportCompanyBlockToShared = async (blockId) => {
        try {
            const response = await GlobalRequest.patchRequest(`${COMPANY_URL}/${blockId}/export`);
            if (response.statusCode === 201) {
                return true;
            }
            return false;
        } catch (error) {
            showToast(error.response, 'Failed to export company attribute');
        }
    };

    return {
        // states
        company_id,
        loading,
        params,
        block,
        blocks,
        totalBlocks,
        // actions
        fetchAllBlocks,
        createBlock,
        updateBlock,
        fetchBlockDetails,
        deleteBlock,
        setBlockDefault,
        importSharedBlocksToCompany,
        exportCompanyBlockToShared,
    };
});
